import { useContext, useEffect, useState } from 'react'
import axios from 'axios'

import { GrowlContext } from 'Context'

// Helpers
const isObject = (val) =>
  val !== null && typeof val === 'object' && !Array.isArray(val)

const extractLaravelPayload = (responseData) => {
  if (!isObject(responseData)) {
    return {
      data: responseData,
      meta: {}
    }
  }

  // Laravel resource format
  if ('data' in responseData) {
    const { data, ...rest } = responseData
    return {
      data,
      meta: rest
    }
  }

  // Plain object response
  return {
    data: responseData,
    meta: {}
  }
}

const useGetData = (url, initialValue, observe, transform, debounce = false) => {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(initialValue)
  const [meta, setMeta] = useState({})
  const { displayError } = useContext(GrowlContext)

  useEffect(() => {
    const source = axios.CancelToken.source()

    const fetchData = async () => {
      setIsLoading(true)

      try {
        const result = await axios(url, { cancelToken: source.token })
        const responseData = result?.data

        let finalData
        let finalMeta = {}

        if (transform) {
          finalData = transform(responseData)
        } else {
          const extracted = extractLaravelPayload(responseData)
          finalData = extracted.data
          finalMeta = extracted.meta
        }

        setData(finalData)
        setMeta(finalMeta)
      } catch (error) {
        if (axios.isCancel(error)) return

        setData(initialValue)
        setMeta({})
        displayError(error)
      } finally {
        setIsLoading(false)
      }
    }

    if (url && !debounce) {
      fetchData()
    }

    return () => {
      source.cancel('Cancelling in cleanup')
    }

    // eslint-disable-next-line
  }, observe ? [url, ...observe] : [url])

  return [isLoading, data, setData, meta]
}

export default useGetData